/**
 * Formula 行（または全文）上で、カーソルが関数呼び出しの括弧内にあるときの
 * 関数名と activeParameter（0 始まり）を推定する軽量ロジック。
 * 文字列・括弧ネスト・中カッコ `{...}` 内はカンマカウントから除外。
 */

/** シグネチャヘルプ用：直前テキストから呼び出し位置を推定 */
export function getFormulaCallContext(
  textBeforeCursor: string,
): { name: string; activeParameter: number } | null {
  const openIdx = findMatchingOpenParenIndex(textBeforeCursor);
  if (openIdx < 0) return null;
  const inner = textBeforeCursor.slice(openIdx + 1);
  const activeParameter = countTopLevelCommas(inner);
  const name = extractIdentifierBefore(textBeforeCursor, openIdx);
  if (!name) return null;
  return { name, activeParameter };
}

/** signatureLabel 内で各引数 label の [開始, 終了) 文字オフセット（Monaco ParameterInformation 用） */
export function formulaParameterRangesInSignature(
  signatureLabel: string,
  paramLabels: readonly string[],
): [number, number][] {
  const open = signatureLabel.indexOf('(');
  if (open < 0) return [];
  let from = open + 1;
  const ranges: [number, number][] = [];
  for (const label of paramLabels) {
    const i = signatureLabel.indexOf(label, from);
    if (i < 0) {
      ranges.push([0, 0]);
      continue;
    }
    ranges.push([i, i + label.length]);
    from = i + label.length;
  }
  return ranges;
}

function findMatchingOpenParenIndex(before: string): number {
  let depth = 0;
  for (let i = before.length - 1; i >= 0; i--) {
    const ch = before[i];
    if (ch === "'" || ch === '"') {
      const quote = ch;
      i--;
      while (i >= 0) {
        if (before[i] === '\\') {
          i -= 1;
          if (i >= 0) i -= 1;
          continue;
        }
        if (before[i] === quote) {
          i -= 1;
          break;
        }
        i -= 1;
      }
      continue;
    }
    if (ch === ')') depth++;
    else if (ch === '(') {
      if (depth === 0) return i;
      depth--;
    }
  }
  return -1;
}

function countTopLevelCommas(fragment: string): number {
  let depthParen = 0;
  let depthBrace = 0;
  let inSingle = false;
  let inDouble = false;
  let commas = 0;
  for (let k = 0; k < fragment.length; k++) {
    const ch = fragment[k];
    if (inSingle) {
      if (ch === '\\' && k + 1 < fragment.length) {
        k++;
        continue;
      }
      if (ch === "'") inSingle = false;
      continue;
    }
    if (inDouble) {
      if (ch === '\\' && k + 1 < fragment.length) {
        k++;
        continue;
      }
      if (ch === '"') inDouble = false;
      continue;
    }
    if (ch === "'") {
      inSingle = true;
      continue;
    }
    if (ch === '"') {
      inDouble = true;
      continue;
    }
    if (ch === '{') {
      depthBrace++;
      continue;
    }
    if (ch === '}' && depthBrace > 0) {
      depthBrace--;
      continue;
    }
    if (depthBrace > 0) continue;

    if (ch === '(') depthParen++;
    else if (ch === ')') depthParen = Math.max(0, depthParen - 1);
    else if (ch === ',' && depthParen === 0) commas++;
  }
  return commas;
}

function extractIdentifierBefore(text: string, endExclusive: number): string | null {
  let j = endExclusive - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return null;
  const ch = text[j]!;
  if (!/[A-Za-z_]/.test(ch)) return null;
  let start = j;
  while (start > 0 && /[\w]/.test(text[start - 1]!)) start--;
  return text.slice(start, j + 1);
}
